import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';
import VehicleCompliancePanel from '../../../components/Machinery/VehicleCompliancePanel';

const EditVehicle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    code: '', name: '', brand: '', model: '', license_plate: '', year: new Date().getFullYear(),
    color: '', type: 'pickup', category_id: '', status: 'active',     propio: 'propio', control_hour_meter: false,
    control_mileage: false, maintenance_alert_km_interval: '', last_maintenance_km: '', notes: ''
  });
  const [complianceDates, setComplianceDates] = useState({ soat_expires_at: '', revision_tecnica_expires_at: '' });

  useEffect(() => {
    loadVehicle();
  }, [id]);

  useEffect(() => {
    const loadCategories = async () => {
      const res = await machineryService.getCategories({ scope: 'vehicle' });
      if (res.success && Array.isArray(res.data)) {
        setCategories(res.data);
      }
    };
    loadCategories();
  }, []);

  const loadVehicle = async () => {
    setLoading(true);
    try {
      const res = await machineryService.getMachineryFormData({ vehicle_id: id });
      if (res.success && res.data?.vehicle) {
        const v = res.data.vehicle;
        setFormData({
          code: v.code || '', name: v.name || '', brand: v.brand || '', model: v.model || '',
          license_plate: v.license_plate || '', year: v.year || new Date().getFullYear(),
          color: v.color || '', type: v.type || 'pickup', status: v.status || 'active',
          category_id: v.category_id != null ? String(v.category_id) : '',
          propio: v.propio || 'propio',
          control_hour_meter: !!v.control_hour_meter,
          control_mileage: !!v.control_mileage,
          maintenance_alert_km_interval: v.maintenance_alert_km_interval != null ? String(v.maintenance_alert_km_interval) : '',
          last_maintenance_km: v.last_maintenance_km != null ? String(v.last_maintenance_km) : '',
          notes: v.notes || ''
        });
        setComplianceDates({
          soat_expires_at: v.soat_expires_at ? String(v.soat_expires_at).split('T')[0] : '',
          revision_tecnica_expires_at: v.revision_tecnica_expires_at ? String(v.revision_tecnica_expires_at).split('T')[0] : '',
        });
      } else {
        setToast({ isOpen: true, message: res.message || 'Vehículo no encontrado', type: 'error' });
        setTimeout(() => navigate('/machinery/vehicles'), 2000);
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar el vehículo', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        year: parseInt(formData.year, 10) || new Date().getFullYear(),
        category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
        maintenance_alert_km_interval: formData.maintenance_alert_km_interval ? parseInt(formData.maintenance_alert_km_interval, 10) : null,
        last_maintenance_km: formData.last_maintenance_km ? parseInt(formData.last_maintenance_km, 10) : null
      };
      const response = await machineryService.updateVehicle(id, data);
      if (response.success) {
        setToast({ isOpen: true, message: 'Vehículo actualizado exitosamente', type: 'success' });
        setTimeout(() => navigate('/machinery/vehicles'), 1500);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al actualizar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al actualizar el vehículo', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando vehículo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Editar Vehículo</h1>
          <p className="text-gray-600 mt-1">Modifique la información del vehículo</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Código *</label>
              <input type="text" name="code" value={formData.code} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Marca *</label>
              <input type="text" name="brand" value={formData.brand} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Modelo *</label>
              <input type="text" name="model" value={formData.model} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Placa *</label>
              <input type="text" name="license_plate" value={formData.license_plate} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
              <input type="number" name="year" value={formData.year} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
              <input type="text" name="color" value={formData.color} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
              <select name="type" value={formData.type} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="pickup">Pickup</option>
                <option value="volquete">Volquete</option>
                <option value="furgon">Furgón</option>
                <option value="camion">Camión</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Categoría (opcional)</label>
              <select name="category_id" value={formData.category_id} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="">Sin categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Estado *</label>
              <select name="status" value={formData.status} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="active">Activo</option>
                <option value="maintenance">En Mantenimiento</option>
                <option value="inactive">Inactivo</option>
                <option value="retired">Retirado</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">¿Es propio? *</label>
              <select name="propio" value={formData.propio} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="propio">Propio</option>
                <option value="alquilado">Alquilado</option>
              </select>
            </div>
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Control de uso</label>
              <p className="text-sm text-gray-500 mb-3">Indique si se llevará control de horómetro y/o kilometraje para este vehículo (puede marcar ambos)</p>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="control_hour_meter" checked={!!formData.control_hour_meter} onChange={handleChange} className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-gray-700">Controlar horómetro</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="control_mileage" checked={!!formData.control_mileage} onChange={handleChange} className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-gray-700">Controlar kilometraje</span>
                </label>
              </div>
            </div>
            <div className="md:col-span-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Alerta de mantenimiento (por kilometraje)</label>
              <p className="text-sm text-gray-500 mb-3">Si define un intervalo (ej. 50 000), el sistema mostrará una alerta en Mantenimientos cuando el vehículo se acerque o supere ese kilometraje.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo de alerta (km)</label>
                  <input type="number" name="maintenance_alert_km_interval" value={formData.maintenance_alert_km_interval} onChange={handleChange} min="1" placeholder="Ej. 50000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Último mantenimiento en (km)</label>
                  <input type="number" name="last_maintenance_km" value={formData.last_maintenance_km} onChange={handleChange} min="0" placeholder="Opcional" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
            </div>
            <VehicleCompliancePanel
              vehicleId={id}
              currentSoat={complianceDates.soat_expires_at}
              currentRevision={complianceDates.revision_tecnica_expires_at}
              onUpdated={loadVehicle}
            />
            <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button type="submit" disabled={saving} className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
            <button type="button" onClick={() => navigate('/machinery/vehicles')} className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors">
              Cancelar
            </button>
          </div>
        </form>
      </div>
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default EditVehicle;
